/* eslint-disable react/forbid-prop-types */
/* eslint-disable no-const-assign */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { BaseTemplate } from '@gcc/configurator';
import ChoiceBoxSection from '@gcc/pip-components/dist/ChoiceBoxSection';
import ChoiceBoxInfoCard from '@gcc/pip-components/dist/ChoiceBoxInfoCard';
import Markdown from '@gcc/pip-components/dist/Markdown';
import isEmpty from 'lodash/isEmpty';
import flow from 'lodash/flow';
import find from 'lodash/find';
import _filter from 'lodash/filter';
import _orderBy from 'lodash/orderBy';
import _map from 'lodash/map';
import _reject from 'lodash/reject';
import withIsMobile from '@gcc/pip-components/dist/withIsMobile';
import { getStore } from '../../store';
import FlooringCard from '../../components/accordion';
import { findTaggedValue } from '../../utils/tags';
import { trackPaddingSelection } from '../../utils/analytics/trackEvents';

import {
  Description,
  ChoiceBoxSectionContainer,
  SectionWrapper,
  ChoiceWrapper,
  IeWrapper,
  InfoCardFlexContent,
  StyledCarousel,
  Divider,
  PriceContainer,
  ProductInfoContainer,
  ProductOrder
} from './styles';
import { boldStart } from '../../utils/boldCovention';
import { extractBoldText, noBoldText } from '../../utils/extractText';
import ConfiguratorTemplate from '../ConfiguratorTemplate';
import { selectSelectedCarpetChoice } from '../../selectors/pricing';
import { selectSelectedSkuData } from '../../selectors/skuDataSelectors';
import {
  selectedCarpetPad,
  getFetchedCarpetPads,
  getCurrentCarpetChoices,
  getCurrentCarpetPadChoices
} from '../../selectors/carpetPad';
import { getSelectedCarpetPad } from '../../actions';
import { updatePath } from '../../actions/pathActions';
import {
  tablet,
  desktop,
  mobileMaxWidth,
} from '../../utils/constants';

function CarpetPadTemplate({ option, onChoiceSelected }) {
  const selectedChoice = useSelector(selectSelectedCarpetChoice);
  const availableCarpetPads = useSelector(getFetchedCarpetPads);
  const availableCarpets = useSelector(getCurrentCarpetChoices);
  const currentPadChoices = useSelector(getCurrentCarpetPadChoices);
  const padSelected = useSelector(selectedCarpetPad);
  const selectedSkuData = useSelector(selectSelectedSkuData);

  const [currentPadSku, setCurrentPadSku] = useState();
  const dispatch = useDispatch();

  // This is all for responsiveness???
  const mobileMediaQueryList = typeof window !== 'undefined' ? window.matchMedia(`(max-width: ${mobileMaxWidth})`) : null;
  const tabletMediaQueryList = typeof window !== 'undefined' ? window.matchMedia(`(min-width: ${tablet}) and (max-width: 1023px)`) : null;
  const desktopMediaQueryList = typeof window !== 'undefined' ? window.matchMedia(`(min-width: ${desktop})`) : null;
  const [isMobile, setIsMobile] = useState(() => mobileMediaQueryList?.matches ?? false);
  const [isDesktop, setIsDesktop] = useState(() => desktopMediaQueryList?.matches ?? false);
  const [isTablet, setIsTablet] = useState(() => tabletMediaQueryList?.matches ?? false);
  const tabletHandleMediaChange = (evt) => setIsTablet(evt.matches);
  const desktopHandleMediaChange = (evt) => setIsDesktop(evt.matches);
  const mobileHandleMediaChange = (evt) => setIsMobile(evt.matches);

  // this handles responsiveness of the flooring app
  useEffect(() => {
    if (typeof window === 'undefined' || !mobileMediaQueryList || !tabletMediaQueryList || !desktopMediaQueryList) return;

    if (mobileMediaQueryList.addEventListener) {
      mobileMediaQueryList.addEventListener(
        'resize DOMContentLoaded',
        mobileHandleMediaChange,
      );
      mobileMediaQueryList.addEventListener(
        'resize DOMContentLoaded',
        mobileHandleMediaChange,
      );
      tabletMediaQueryList.addEventListener(
        'resize DOMContentLoaded',
        tabletHandleMediaChange,
      );
      desktopMediaQueryList.addEventListener(
        'resize DOMContentLoaded',
        desktopHandleMediaChange,
      );
    } else {
      // safari 13- fallbacks
      mobileMediaQueryList.addListener(mobileHandleMediaChange);
      tabletMediaQueryList.addListener(tabletHandleMediaChange);
      desktopMediaQueryList.addListener(desktopHandleMediaChange);
    }

    return () => {
      if (mobileMediaQueryList.removeEventListener) {
        mobileMediaQueryList.removeEventListener(
          'resize DOMContentLoaded',
          mobileHandleMediaChange,
        );
        tabletMediaQueryList.removeEventListener(
          'resize DOMContentLoaded',
          tabletHandleMediaChange,
        );
        desktopMediaQueryList.removeEventListener(
          'resize DOMContentLoaded',
          desktopHandleMediaChange,
        );
      } else {
        // safari 13- fallbacks
        mobileMediaQueryList.removeListener(mobileHandleMediaChange);
        tabletMediaQueryList.removeListener(tabletHandleMediaChange);
        desktopMediaQueryList.removeListener(desktopHandleMediaChange);
      }
    };
  }, []);

  useEffect(() => {
    if (!isEmpty(padSelected)) { setCurrentPadSku(findTaggedValue(padSelected.tags, 'pad')); }
  }, [padSelected]);

  useEffect(() => { if (currentPadSku) { trackPaddingSelection(currentPadSku); } }, [currentPadSku]);

  const onChoiceSelect = (choice) => {
    onChoiceSelected(choice.optionId, choice.id, choice.value);
    dispatch(updatePath());
    dispatch(getSelectedCarpetPad(choice));
  };

  useEffect(() => {
    if (!isEmpty(selectedChoice)) {
      const carpetHdmsSku = selectedSkuData?.hdmsSku || findTaggedValue(selectedChoice.tags, 'hdmssku');
      if (!isEmpty(availableCarpets[carpetHdmsSku])) {
        const { skuNumber: padSku } = find(availableCarpets[carpetHdmsSku], (pad) => pad.choiceType === 'Recommended');
        const recommendedPadByChoice = currentPadChoices[padSku];
        if (isEmpty(padSelected) && !isEmpty(recommendedPadByChoice)) {
          onChoiceSelect(recommendedPadByChoice);
        }
      }
    }
  }, [selectedChoice, availableCarpets, currentPadChoices]);

  const isRecommended = (choice) => !isEmpty(selectedChoice) && choice.padChoiceType === 'Recommended';
  const isUpgrade = (choice) => !isEmpty(selectedChoice) && choice.padChoiceType === 'Upgrade';

  const choiceBoxSection = (
    <ChoiceBoxSection>
      <SectionWrapper>
        {!isEmpty(availableCarpetPads) && Object.values(availableCarpetPads).map((choice) => (
          <ChoiceWrapper key={choice.id}>
            <IeWrapper onClick={() => onChoiceSelect(choice)}>
              {isRecommended(choice) && <span className="highlightLabel">Recommended</span>}
              {isUpgrade(choice) && <span className="highlightLabel">Upgrade</span>}
              <ChoiceBoxInfoCard imageUrl={choice.media ? choice.mediaUrl : null} selected={choice.isSelected}>
                <Divider />
                <ProductInfoContainer>
                  <ProductOrder>{choice.shortLabel}</ProductOrder>
                  <PriceContainer>${choice.padPrice} /sq. ft.</PriceContainer>
                </ProductInfoContainer>
                <InfoCardFlexContent>
                  {choice.longLabel ? (
                    <Markdown>
                      {choice.longLabel}
                    </Markdown>
                  ) : (
                    <>&nbsp;</>
                  )}
                </InfoCardFlexContent>
              </ChoiceBoxInfoCard>
            </IeWrapper>
          </ChoiceWrapper>
        ))}
      </SectionWrapper>
    </ChoiceBoxSection>
  );

  return (
    <BaseTemplate store={getStore()} option={option.id}>
      <FlooringCard title={option.shortLabel} testId="select-carpet-padding">
        <Description>
          {option.longLabel.includes(boldStart) ? (
            <>
              <span>{noBoldText(option.longLabel)}
                <b>{extractBoldText(option.longLabel)}</b>
              </span>
            </>
          ) : (<span>{option.longLabel}</span>)}
        </Description>
        {(isDesktop) && (
          <ChoiceBoxSectionContainer>
            {choiceBoxSection}
          </ChoiceBoxSectionContainer>
        )}
        {(isMobile || isTablet) && (
          <StyledCarousel>
            <ChoiceBoxSectionContainer>
              {choiceBoxSection}
            </ChoiceBoxSectionContainer>
          </StyledCarousel>
        )}
      </FlooringCard>
    </BaseTemplate>
  );
}

CarpetPadTemplate.propTypes = {
  option: PropTypes
    .shape({
      id: PropTypes.number.isRequired,
      longLabel: PropTypes.string.isRequired,
      messages: PropTypes.array,
      tags: PropTypes.array,
      shortLabel: PropTypes.string,
    })
    .isRequired,
  onChoiceSelected: PropTypes.func.isRequired,
};

export default flow(withIsMobile)(CarpetPadTemplate);
